import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {number | null} */
  #colorOptionIndex = null;

  /** @type {number | null} */
  #sizeOptionIndex = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupOptionDropdowns();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Detects the actual Globo option indexes for Color and Size.
   * Supports both Color -> Size and Size -> Color.
   */
  #detectOptionIndexes() {
    const optionGroups = new Map();

    document.querySelectorAll('input[data-globoswatchindex]').forEach((input) => {
      const optionIndex = input.dataset.globoswatchindex?.replace('option', '');
      if (!optionIndex) return;

      const group =
        input.closest('.swatch--gl') ||
        input.closest('.swatch') ||
        input.closest('[data-option-index]') ||
        input.parentElement;

      const groupText = group?.textContent?.trim().toLowerCase() || '';
      const optionName =
        input.getAttribute('data-option-name')?.trim().toLowerCase() ||
        input.getAttribute('aria-label')?.trim().toLowerCase() ||
        '';

      const text = `${groupText} ${optionName}`;

      if (!optionGroups.has(optionIndex)) {
        optionGroups.set(optionIndex, text);
      }
    });

    this.#colorOptionIndex = null;
    this.#sizeOptionIndex = null;

    for (const [index, text] of optionGroups) {
      if (this.#colorOptionIndex === null && /color|colour/.test(text)) {
        this.#colorOptionIndex = Number(index);
      }

      if (this.#sizeOptionIndex === null && /size/.test(text)) {
        this.#sizeOptionIndex = Number(index);
      }
    }

    // Preserve the original behavior as a fallback.
    if (this.#colorOptionIndex === null && optionGroups.has('1')) {
      this.#colorOptionIndex = 1;
    }

    if (this.#sizeOptionIndex === null && optionGroups.has('2')) {
      this.#sizeOptionIndex = 2;
    }
  }

  /**
   * Selects a Globo option without scrolling the page.
   */
  #selectGloboOption(optionIndex, value) {
    if (!optionIndex || !value) return null;

    const inputs = document.querySelectorAll(
      `input[data-globoswatchindex="option${optionIndex}"]`
    );

    for (const input of inputs) {
      if (input.value === value) {
        if (!input.checked) {
          input.click();
        }
        return input;
      }
    }

    return null;
  }

  /**
   * Gets the currently selected Globo value for an option.
   */
  #getSelectedGloboValue(optionIndex) {
    if (!optionIndex) return null;

    const input = document.querySelector(
      `input[data-globoswatchindex="option${optionIndex}"]:checked`
    );

    return input?.value?.trim() || null;
  }

  /**
   * Applies the sticky dropdown defaults to Globo when the PDP has
   * no selection yet.
   */
  #applyDefaultGloboSelections() {
    this.#detectOptionIndexes();

    const colorSelect = this.querySelector('.sticky-add-to-cart__color-select');
    const sizeSelect = this.querySelector('.sticky-add-to-cart__size-select');

    if (
      colorSelect &&
      this.#colorOptionIndex &&
      !this.#getSelectedGloboValue(this.#colorOptionIndex)
    ) {
      const firstColor = colorSelect.options[0]?.value;

      if (firstColor) {
        this.#selectGloboOption(this.#colorOptionIndex, firstColor);
      }
    }

    if (
      sizeSelect &&
      this.#sizeOptionIndex &&
      !this.#getSelectedGloboValue(this.#sizeOptionIndex)
    ) {
      const firstSize = sizeSelect.options[0]?.value;

      if (firstSize) {
        this.#selectGloboOption(this.#sizeOptionIndex, firstSize);
      }
    }

    this.#syncDropdownsFromGlobo();
  }

  /**
   * Sets up the Color and Size dropdowns in the sticky bar.
   * Both dropdowns sync directly with Globo's actual option inputs.
   */
  #setupOptionDropdowns() {
    this.#detectOptionIndexes();

    const colorSelect = this.querySelector('.sticky-add-to-cart__color-select');
    const sizeSelect = this.querySelector('.sticky-add-to-cart__size-select');

    if (colorSelect) {
      const seenColors = new Set();

      Array.from(colorSelect.options).forEach((option) => {
        if (seenColors.has(option.value)) {
          option.remove();
        } else {
          seenColors.add(option.value);
        }
      });

      const selectedColor = this.#getSelectedGloboValue(this.#colorOptionIndex);

      if (selectedColor) {
        colorSelect.value = selectedColor;
      }

      colorSelect.addEventListener('change', async (event) => {
        const selectedColor = event.target.value;

        if (this.#getSelectedGloboValue(this.#colorOptionIndex) === selectedColor) return;

        this.#selectGloboOption(this.#colorOptionIndex, selectedColor);

        // Give Globo time to update the product form/variant.
        await new Promise((resolve) => setTimeout(resolve, 150));

        this.#syncDropdownsFromGlobo();
      }, { signal: this.#abortController.signal });
    }

    if (sizeSelect) {
      const seenSizes = new Set();

      Array.from(sizeSelect.options).forEach((option) => {
        if (seenSizes.has(option.value)) {
          option.remove();
        } else {
          seenSizes.add(option.value);
        }
      });

      const selectedSize = this.#getSelectedGloboValue(this.#sizeOptionIndex);

      if (selectedSize) {
        sizeSelect.value = selectedSize;
      }

      sizeSelect.addEventListener('change', async (event) => {
        const selectedSize = event.target.value;

        if (this.#getSelectedGloboValue(this.#sizeOptionIndex) === selectedSize) return;

        // Use Globo's actual option2 input so the PDP and Shopify
        // variant selection update together.
        this.#selectGloboOption(this.#sizeOptionIndex, selectedSize);

        // Give Globo time to update Shopify's actual variant.
        await new Promise((resolve) => setTimeout(resolve, 150));

        this.#syncDropdownsFromGlobo();
      }, { signal: this.#abortController.signal });
    }

    // If the PDP has not selected Color and/or Size yet,
    // make the first sticky choices the actual PDP selections.
    this.#applyDefaultGloboSelections();
  }

  /**
   * Keeps the sticky dropdowns matched to Globo's actual selections.
   */
  #syncDropdownsFromGlobo() {
    this.#detectOptionIndexes();

    const colorSelect = this.querySelector('.sticky-add-to-cart__color-select');
    const sizeSelect = this.querySelector('.sticky-add-to-cart__size-select');

    const selectedColor = this.#getSelectedGloboValue(this.#colorOptionIndex);
    const selectedSize = this.#getSelectedGloboValue(this.#sizeOptionIndex);

    if (colorSelect && selectedColor && colorSelect.value !== selectedColor) {
      colorSelect.value = selectedColor;
    }

    if (sizeSelect && selectedSize && sizeSelect.value !== selectedSize) {
      sizeSelect.value = selectedSize;
    }
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#detectOptionIndexes();

    const colorSelect = this.querySelector('.sticky-add-to-cart__color-select');
    const sizeSelect = this.querySelector('.sticky-add-to-cart__size-select');

    let changed = false;

    if (colorSelect && this.#colorOptionIndex) {
      const colorValue = colorSelect.value || colorSelect.options[0]?.value;

      if (
        colorValue &&
        this.#getSelectedGloboValue(this.#colorOptionIndex) !== colorValue
      ) {
        this.#selectGloboOption(this.#colorOptionIndex, colorValue);
        changed = true;
      }
    }

    if (sizeSelect && this.#sizeOptionIndex) {
      const sizeValue = sizeSelect.value || sizeSelect.options[0]?.value;

      if (
        sizeValue &&
        this.#getSelectedGloboValue(this.#sizeOptionIndex) !== sizeValue
      ) {
        this.#selectGloboOption(this.#sizeOptionIndex, sizeValue);
        changed = true;
      }
    }

    /*
     * If the sticky selections changed, wait for Horizon's actual
     * variant update event before clicking the real Add to Cart button.
     *
     * This prevents the first click from only changing the variant
     * and requiring a second click to add it to the cart.
     */
    if (changed) {
      await new Promise((resolve) => {
        let finished = false;

        const finish = () => {
          if (finished) return;
          finished = true;
          clearTimeout(timeout);
          this.removeEventListener(ThemeEvents.variantUpdate, finish);
          resolve();
        };

        const timeout = setTimeout(finish, 1000);

        this.addEventListener(ThemeEvents.variantUpdate, finish, {
          once: true,
        });
      });
    }

    this.#syncDropdownsFromGlobo();

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
    this.#setupOptionDropdowns();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
